import React from 'react';
import { ClickAwayListener, Paper, Popper } from '@mui/material';
import { de, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { cn } from '../../utils/utils';

// Shared building blocks for the react-day-picker based fields
// (`SingleDatePicker`, `DateRangeField`) and the dashboard `DateRangePicker`.
// Extracted so the trigger markup, popover shell, tenant-primary token
// overrides and locale resolution live in exactly one place instead of being
// copied per picker (No-Duplication rule).

// Map react-day-picker's accent tokens onto the tenant primary so chevrons,
// today-ring, selected day and range endpoints/middle all follow the theme.
// Must be applied on the DayPicker root because `.rdp-root` re-declares these
// vars — setting them on a parent gets shadowed.
export const RDP_PRIMARY_TOKENS: React.CSSProperties = {
    ['--rdp-accent-color' as string]: 'rgb(var(--color-primary))',
    ['--rdp-accent-background-color' as string]: 'rgba(var(--color-primary), 0.12)',
    ['--rdp-today-color' as string]: 'rgb(var(--color-primary))',
    ['--rdp-range_middle-background-color' as string]: 'rgba(var(--color-primary), 0.12)',
    ['--rdp-range_start-date-background-color' as string]: 'rgb(var(--color-primary))',
    ['--rdp-range_end-date-background-color' as string]: 'rgb(var(--color-primary))',
} as React.CSSProperties;

// DayPicker props shared verbatim by every picker (the per-instance props —
// mode/selected/onSelect/locale/disabled/startMonth/endMonth — are spread in
// alongside this at each call site).
export const COMMON_DAY_PICKER_PROPS = {
    captionLayout: 'dropdown',
    numberOfMonths: 1,
    showOutsideDays: true,
    autoFocus: true,
    style: RDP_PRIMARY_TOKENS,
};

// Resolve the date-fns locale from the active i18n language (de vs en).
export function useDayPickerLocale() {
    const { i18n } = useTranslation();
    return React.useMemo(() => {
        const lang = (i18n.language || 'en').toLowerCase();
        return lang.startsWith('de') ? de : enUS;
    }, [i18n.language]);
}

export type DateMatcher = { before?: Date, after?: Date } | ((date: Date) => boolean);

export interface BoundMatcherOptions {
    disablePast?: boolean;
    disableFuture?: boolean;
    minDate?: Dayjs;
    maxDate?: Dayjs;
}

// Base react-day-picker `disabled` matchers from disablePast/disableFuture and
// explicit min/max bounds. Callers append their own allow-list matcher(s).
// Order doesn't matter — RDP disables when any matcher fires.
export function buildBoundMatchers(opts: BoundMatcherOptions): DateMatcher[] {
    const matchers: DateMatcher[] = [];
    const today = dayjs().startOf('day');
    if (opts.disablePast) matchers.push({ before: today.toDate() });
    if (opts.disableFuture) matchers.push({ after: today.toDate() });
    if (opts.minDate) matchers.push({ before: opts.minDate.startOf('day').toDate() });
    if (opts.maxDate) matchers.push({ after: opts.maxDate.startOf('day').toDate() });
    return matchers;
}

// Month bounds for the caption year/month dropdown. Falls back to a generous
// span around today (`backYears` / `forwardYears`) when no explicit bound.
export function useDropdownMonthRange(
    minDate: Dayjs | undefined,
    maxDate: Dayjs | undefined,
    backYears: number,
    forwardYears: number,
) {
    const startMonth = React.useMemo(
        () => (minDate ?? dayjs().subtract(backYears, 'year')).toDate(),
        [minDate, backYears],
    );
    const endMonth = React.useMemo(
        () => (maxDate ?? dayjs().add(forwardYears, 'year')).toDate(),
        [maxDate, forwardYears],
    );
    return { startMonth, endMonth };
}

// Popover open/anchor/focus state + trigger/close handlers shared by the
// single and range date fields.
export function useDatePickerPopoverState(disabled?: boolean) {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const [open, setOpen] = React.useState(false);
    const [focused, setFocused] = React.useState(false);

    const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;
        setAnchor(event.currentTarget);
        setOpen(true);
    };
    const close = () => {
        setOpen(false);
        setFocused(false);
    };
    return { anchor, open, focused, setFocused, handleTriggerClick, close };
}

export type DatePickerPopoverState = ReturnType<typeof useDatePickerPopoverState>;

interface DatePickerTriggerProps {
    // Text rendered inside the trigger (formatted value, placeholder, or a
    // single space to keep the height when empty with a floating label).
    text: string;
    // Whether a value is selected — drives placeholder colour + label float.
    hasValue: boolean;
    label?: string;
    disabled?: boolean;
    open: boolean;
    focused: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onFocus: () => void;
    onBlur: () => void;
    className?: string;
    // The popover (e.g. `CalendarPopover`) rendered as a sibling of the button.
    children?: React.ReactNode;
}

// Floating-label, MUI-input-style trigger shared by the single/range date
// fields so both sit flush with the surrounding Tailwind inputs.
export function DatePickerTrigger(props: DatePickerTriggerProps) {
    const isFloating = props.focused || props.open || props.hasValue;
    return (
        <div className={cn('relative w-full', props.className)}>
            <button
                type={'button'}
                onClick={props.onClick}
                onFocus={props.onFocus}
                onBlur={props.onBlur}
                disabled={props.disabled}
                className={cn(
                    'flex items-center justify-between gap-2 w-full text-left',
                    'py-2 px-4 rounded-xl bg-transparent',
                    'border transition-all outline-none',
                    props.disabled
                        ? 'border-foreground/20 cursor-not-allowed text-foreground/40'
                        : props.open || props.focused
                            ? 'border-primary text-foreground'
                            : 'border-foreground text-foreground',
                )}
            >
                <span className={cn('text-sm truncate', !props.hasValue && 'text-foreground/50')}>
                    {props.text}
                </span>
                <CalendarIcon
                    className={cn(
                        'h-4 w-4 shrink-0',
                        props.disabled ? 'text-foreground/30' : 'text-primary',
                    )}
                    aria-hidden={'true'}
                />
            </button>
            {props.label && (
                <label
                    className={cn(
                        'absolute left-3 pointer-events-none transition-all duration-200',
                        'truncate max-w-[calc(100%-2.5rem)]',
                        isFloating
                            ? '-top-2.5 text-xs bg-white px-1 text-foreground'
                            : 'top-1/2 -translate-y-1/2 text-sm text-foreground/50',
                        props.disabled && 'text-foreground/30',
                    )}
                >
                    {props.label}
                </label>
            )}
            {props.children}
        </div>
    );
}

interface CalendarPopoverProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
}

// Popover shell (Popper + click-away + card) shared by every picker.
export function CalendarPopover({ anchorEl, open, onClose, children }: CalendarPopoverProps) {
    if (!anchorEl) return null;
    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement={'bottom-start'}
            // Follow the theme's overlay layer instead of a hardcoded 1300, so
            // the calendar clears the `CustomModal` backdrop when a picker is
            // rendered inside a modal (see `material-ui/theme.ts`). `+ 1`
            // because pickers are also rendered inside MUI `Dialog`s (e.g. the
            // CSV export on `BookingsPage`), which sit on `modal` itself — at
            // an equal value the calendar would only win by portal insertion
            // order, which is not something to rely on.
            sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
        >
            <ClickAwayListener onClickAway={onClose}>
                <Paper className={'mt-2 rounded-xl shadow-lg border border-gray-100 bg-white p-3'}>
                    {children}
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
}

interface DayPickerFieldProps {
    state: DatePickerPopoverState;
    text: string;
    hasValue: boolean;
    label?: string;
    disabled?: boolean;
    className?: string;
    // The `<DayPicker/>` element, rendered inside the popover.
    children: React.ReactNode;
}

// Full field shell = floating-label trigger + calendar popover. Both
// `SingleDatePicker` and `DateRangeField` render one of these with their own
// DayPicker as children, so the trigger/popover wiring lives in one place.
export function DayPickerField(props: DayPickerFieldProps) {
    const { state } = props;
    return (
        <DatePickerTrigger
            text={props.text}
            hasValue={props.hasValue}
            label={props.label}
            disabled={props.disabled}
            open={state.open}
            focused={state.focused}
            onClick={state.handleTriggerClick}
            onFocus={() => state.setFocused(true)}
            onBlur={() => state.setFocused(false)}
            className={props.className}
        >
            <CalendarPopover anchorEl={state.anchor} open={state.open} onClose={state.close}>
                {props.children}
            </CalendarPopover>
        </DatePickerTrigger>
    );
}
